import React, { FocusEvent } from "react";
import { Field, FieldArray, reduxForm, InjectedFormProps } from "redux-form";
import { TextField, SelectField, Button } from "capitalrx-components";
import { GeneralTypes } from "@/types";

import DrugListForm from "./DrugListForm";

interface DefaultProps {
  path: string;
  sectionTitle: string;
  buttonText: string;
  handleFieldOnBlur: GeneralTypes.EventWithDataHandler<FocusEvent<object>>;
}

type Props = InjectedFormProps & DefaultProps;

export const otherCoverageCodeOptions: Array<GeneralTypes.LabelType> = [
  {
    label: "00 - Not specified/Unknown",
    value: "00",
  },
  {
    label: "01 - No other coverage/Primary",
    value: "01",
  },
  {
    label: "02 - Prior claim covered",
    value: "02",
  },
  {
    label: "03 - Prior claim REJECTED",
    value: "03",
  },
  {
    label: "04 - Prior claim, $0 paid/100% cost share",
    value: "04",
  },
  {
    label: "08 - Coupon transactions ONLY",
    value: "08",
  },
];

const DrugSummaryForm: React.FC<DefaultProps & InjectedFormProps<{}, DefaultProps>> = (props: Props) => {
  const { buttonText, sectionTitle, path, handleFieldOnBlur } = props;

  return (
    <div className="test-claim-section-summary">
      <div className="row caprx-h-center-text">
        <div className="col">
          <div className="test-claim-section-description mb30">{sectionTitle}</div>
        </div>
      </div>
      {/* @ts-ignore */}
      <FieldArray name="drugs" component={DrugListForm} />
      <div className="row caprx-h-center-text">
        <div className="col">
          <Button
            data-testid="test__add_drug"
            extraClasses="test-claim-button mb30"
            buttonType="router-link"
            buttonStyle="outline"
            color="darkblue"
            path={path}
          >
            <span className="caprx-icon-input-plus mr5" />
            {buttonText}
          </Button>
        </div>
      </div>
      <div className="claim-test-divider" />
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="days-supply"
            testId="test__days-supply"
            name="days_supply"
            label="Days Supply"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="daw"
            data-testid="test__daw"
            name="daw"
            label="Dispense as Written (DAW) Code"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="fill-number"
            data-testid="test__fill-number"
            name="fill_number"
            label="Fill Number"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="submission-clarification-code-1"
            data-testid="test__submission-clarification-code-1"
            name="submission_clarification_code_1"
            label="Submission Clarification Code"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="professional-service-code"
            data-testid="test__professional_service_code"
            label="Professional Service Code"
            name="professional_service_code"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="dur-pps-level-of-effort"
            data-testid="test__dur_pps_level_of_effort"
            label="DUR/PPS Level Of Effort Value"
            name="dur_pps_level_of_effort"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="reason-for-service-code"
            data-testid="test__reason_for_service_code"
            label="Reason for Service Code"
            name="reason_for_service_code"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="result-of-service-code"
            data-testid="test__result_of_service_code"
            label="Result of Service Code"
            name="result_of_service_code"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="prescriber-id-qualifier"
            data-testid="test__prescriber_id_qualifier"
            label="Prescriber ID Qualifier"
            name="prescriber_id_qualifier"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="prescriber-id"
            data-testid="test__prescriber_id"
            label="Prescriber ID (NPI)"
            name="prescriber_id"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="pharmacy-service-type"
            data-testid="test__pharmacy_service_type"
            label="Pharmacy Service Type"
            name="pharmacy_service_type"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="place-of-service"
            data-testid="test__place_of_service"
            label="Place of Service"
            name="place_of_service"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={SelectField}
            id="other_coverage_code"
            data-testid="other_coverage_code"
            name="other_coverage_code"
            label="Other Coverage Code"
            type="text"
            options={otherCoverageCodeOptions}
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="other_payer_id_qualifier_1"
            data-testid="other_payer_id_qualifier_1"
            label="Other Payer Qualifier"
            name="other_payer_id_qualifier_1"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="other_payer_id_1"
            data-testid="other_payer_id_1"
            label="Other Payer ID"
            name="other_payer_id_1"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="other_payer_amount_paid_1"
            data-testid="other_payer_amount_paid_1"
            label="Other Payer Amount Paid (OPAP)"
            name="other_payer_amount_paid_1"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="other_payer_patient_responsibility_amount_1"
            data-testid="other_payer_patient_responsibility_amount_1"
            label="Other Payer Patient Responsibility Amount (OPPRA)"
            name="other_payer_patient_responsibility_amount_1"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="other_payer_reject_code_1"
            data-testid="other_payer_reject_code_1"
            label="Other Payer Reject Code"
            name="other_payer_reject_code_1"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="patient-residence"
            data-testid="test__patient_residence"
            label="Patient Residence Code"
            name="patient_residence"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="prescription-origin-code"
            data-testid="prescription_origin_code"
            name="prescription_origin_code"
            label="Prescription Origin Code"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="diagnosis_code_qualifier_1"
            data-testid="diagnosis_code_qualifier_1"
            name="diagnosis_code_qualifier_1"
            label="Diagnosis Code Qualifier (use 02 for ICD-10)"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="diagnosis_code_1"
            data-testid="diagnosis_code_1"
            name="diagnosis_code_1"
            label="Diagnosis Code"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="usual-and-customary-charge"
            data-testid="test__usual_and_customary_charge"
            label="U&C/Submitted Cost"
            name="usual_and_customary_charge"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="ingredient_cost_submitted"
            data-testid="ingredient_cost_submitted"
            name="ingredient_cost_submitted"
            label="Ingredient Cost Submitted"
            onBlur={handleFieldOnBlur}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Field
            component={TextField}
            id="dispensing_fee_submitted"
            data-testid="dispensing_fee_submitted"
            name="dispensing_fee_submitted"
            label="Dispensing Fee Submitted"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="incentive_amount_submitted"
            data-testid="incentive_amount_submitted"
            name="incentive_amount_submitted"
            label="Vaccine Administration Reimbursement Amount"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
          <Field
            component={TextField}
            id="special_packaging_indicator"
            data-testid="special_packaging_indicator"
            label="Special Packaging Indicator"
            name="special_packaging_indicator"
            onBlur={handleFieldOnBlur}
          />
        </div>
        <div className="col">
        </div>
      </div>
    </div>
  );
};
export default reduxForm<{}, DefaultProps>({ form: "TestClaim" })(DrugSummaryForm);
